<template>
  <div>
    <div class="settings-header-with-btn">
      <div class="headline5 mb-4">
        {{ $t(`modals.addGlobalMspPolicy.policies.endpointSecurity.title`) }}
      </div>
      <v-menu v-if="selectedTab === 0 && !disabled" offset-y bottom right z-index="100">
        <template v-slot:activator="{ props }">
          <v-btn
            class="add-btn bg-white"
            variant="outlined"
            rounded
            size="large"
            data-testid="data-governance-add-permission-btn"
            v-bind="props"
          >
            <v-icon class="mr-2" icon="$add"></v-icon>
            {{ $t("general.add") }}
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            :key="action.osType"
            v-for="action in addPolicyActions"
            @click="handleAddPolicy(action)"
          >
            <v-list-item-title>
              {{ action.title }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        v-if="showAddMalwareScanBtn"
        class="add-btn bg-white"
        variant="outlined"
        rounded
        size="large"
        elevation="0"
        data-testid="add-malware-scan-btn"
        @click="addMalwareScanSchedule"
      >
        <v-icon class="mr-2" icon="$add"></v-icon>
        {{ $t("devicesSettings.malwareScan.addBtn") }}
      </v-btn>
    </div>
    <v-tabs
      v-model="selectedTab"
      align-with-title
      color="orange-base"
      background-color="white"
      slider-color="orange-base"
      :height="48"
    >
      <template v-for="(item, index) in tabs" :key="index">
        <v-divider class="mr-8 my-2" v-if="'divider' in item" vertical />
        <v-tab v-else class="mr-8" :height="42" :value="item.id">
          {{ item.name }}
        </v-tab>
      </template>
    </v-tabs>
    <v-window v-model="selectedTab">
      <v-window-item :value="0">
        <div class="mt-2">
          <table-wrapper>
            <endpoint-security-device-posture-table
              :items="mappedPostureData"
              @action-click="handleActionClick"
            />
          </table-wrapper>
        </div>
      </v-window-item>

      <v-window-item :value="1">
        <endpoint-security-ngav-tab-content
          v-model="localValue.ngav"
          :labels="availableLabels"
          :action-not-allowed="disabled"
          @change-risky-ngav-item="onChangeNgavItem($event)"
          @change-initial-scan="onChangeNgavItem('initialDevicesScanEnabled')"
        />
      </v-window-item>

      <v-window-item :value="2">
        <endpoint-security-malware-scan-tab-content
          :items="localValue.devicePosture.malwareScansScheduleSettings"
          :show-skeleton-loader="false"
          @on-action="onMalwareScanAction"
        />
      </v-window-item>

      <v-window-item :value="3">
        <wifi-phishing-card
          v-model:settings="localValue.addons"
          :labels="availableLabels"
          :action-not-allowed="disabled"
        />
      </v-window-item>
    </v-window>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import { defaultEmptyPolicies, GlobalPolicySection } from "@/constants/msp.ts";
import cloneDeep from "lodash/cloneDeep";
import type { GlobalPolicyConfig } from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import { useI18n } from "vue-i18n";
import EndpointSecurityNgavTabContent from "@/components/settings/EndpointSecurityNgavTabContent.vue";
import { useMspDevicesStore } from "@/_store/msp/devices/msp-devices.module.ts";
import { storeToRefs } from "pinia";
import WifiPhishingCard from "@/components/endpoint-security/WifiPhishingCard.vue";
import EndpointSecurityDevicePostureTable from "@/components/endpoint-security/EndpointSecurityDevicePostureTable.vue";
import TableWrapper from "@/components/TableWrapper.vue";
import {
  DevicePostureAction,
  DevicePostureOsType,
  DevicePostureSettings,
} from "@/constants/devices.ts";
import type { DeviceLabel } from "@/_store/devices-settings.module.ts";
import { ModalWidth } from "@/constants/modals.ts";
import AddOrEditPostureModal from "@/components/modals/device-posture/AddOrEditPostureModal.vue";
import {
  type DialogConfig,
  type DialogDataConfig,
  useDialogsStore,
} from "@/_store/dialogs.module.ts";
import {
  mapFormDataToGlobalSettings,
  mapGlobalDevicePostureSettings,
  removeFromGlobalPostureSettings,
} from "@/_helpers/global-policy.ts";
import type {
  DevicePostureAdaptedItem,
  DevicePostureCombinedType,
  DevicePostureFormData,
} from "@/_store/device-posture/devices-posture-settings.module.ts";
import EndpointSecurityMalwareScanTabContent from "@/components/settings/EndpointSecurityMalwareScanTabContent.vue";
import type { MalwareScanItem } from "@/_store/endpoint-security/malware-scan.module.ts";
import MalwareScanModal from "@/components/modals/MalwareScanModal.vue";
import {
  defaultMalwareScanItem,
  MalwareScanActions,
} from "@/constants/endpoint-security-malware-scan.ts";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";

export default defineComponent({
  components: {
    EndpointSecurityMalwareScanTabContent,
    TableWrapper,
    EndpointSecurityDevicePostureTable,
    WifiPhishingCard,
    EndpointSecurityNgavTabContent,
  },
  props: {
    data: {
      type: Object as PropType<GlobalPolicyConfig>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:data"],
  setup(props, { emit }) {
    const mspDevicesStore = useMspDevicesStore();
    const { getLabels } = mspDevicesStore;
    const { availableLabels } = storeToRefs(mspDevicesStore);
    const i18n = useI18n();
    const dialogsStore = useDialogsStore();
    const localValue = ref(
      cloneDeep(
        props.data[GlobalPolicySection.ENDPOINT_SECURITY] ??
          defaultEmptyPolicies[GlobalPolicySection.ENDPOINT_SECURITY]
      )
    );
    const selectedTab = ref(0);

    const tabs = computed(() => [
      { id: 0, type: "tab", name: i18n.t("devicesSettings.tabs.devicePosture") },
      { id: 1, type: "tab", name: i18n.t("devicesSettings.tabs.ngav") },
      { id: 2, name: i18n.t("devicesSettings.tabs.malwareScan") },
      { id: "divider", type: "divider", divider: true },
      { id: 3, type: "tab", name: i18n.t("devicesSettings.tabs.addons") },
    ]);

    const showAddMalwareScanBtn = computed(
      () =>
        selectedTab.value === 2 &&
        !props.disabled &&
        localValue.value.devicePosture.malwareScansScheduleSettings.length > 0
    );

    const mappedPostureData = computed(() => {
      return mapGlobalDevicePostureSettings(localValue.value.devicePosture);
    });

    watch(
      localValue,
      (newVal) => {
        emit("update:data", {
          ...props.data,
          [GlobalPolicySection.ENDPOINT_SECURITY]: newVal,
        });
      },
      { immediate: true, deep: true }
    );

    onMounted(async () => {
      await getLabels();
    });

    const onChangeNgavItem = (
      field:
        | "realTimeMalwareProtectionEnabled"
        | "vssPolicyEnabled"
        | "atcEnabled"
        | "initialDevicesScanEnabled"
    ) => {
      localValue.value.ngav[field] = !localValue.value.ngav[field];
    };

    const handleActionClick = ({
      action,
      policy,
      item,
    }: {
      action: DevicePostureAction;
      policy: DevicePostureCombinedType;
      item: DevicePostureAdaptedItem;
    }) => {
      let dialogConfig;
      switch (action) {
        case DevicePostureAction.REMOVE: {
          dialogConfig = confirmationDialogsConfigConstructor({
            item: {
              ...policy,
              policyType: item.policyType,
              osType: item.osType,
            },
            action,
            callback: ({ item }: { item: DevicePostureFormData }) => {
              localValue.value.devicePosture = removeFromGlobalPostureSettings(
                item,
                localValue.value.devicePosture
              );
            },
            disable: props.disabled,
            text: i18n.t(`modals.${action}.description`),
          });
          break;
        }
        case DevicePostureAction.EDIT:
        default: {
          dialogConfig = componentDialogsConfigConstructor({
            item: {
              ...policy,
              policyType: item.policyType,
              osType: item.osType,
              availableLabels: availableLabels.value,
              skipLabelFetching: true,
            },
            action,
            component: AddOrEditPostureModal,
            width: ModalWidth.LARGE,
            callback: (data: DevicePostureFormData) => {
              localValue.value.devicePosture = mapFormDataToGlobalSettings(
                data,
                localValue.value.devicePosture
              );
            },
            disable: props.disabled,
            scrollable: true,
          });
          break;
        }
      }
      dialogsStore.openDialog(dialogConfig);
    };

    const handleAddPolicy = (
      {
        osType,
        action,
      }: {
        osType: DevicePostureOsType;
        action: DevicePostureAction;
      },
      labels: DeviceLabel[] = [],
      policyType?: DevicePostureSettings
    ) => {
      const config = componentDialogsConfigConstructor({
        action,
        item: {
          osType,
          labels,
          policyType,
          availableLabels: availableLabels.value,
          skipLabelFetching: true,
        },
        width: ModalWidth.LARGE,
        component: AddOrEditPostureModal,
        callback: (data: DevicePostureFormData) => {
          localValue.value.devicePosture = mapFormDataToGlobalSettings(
            data,
            localValue.value.devicePosture
          );
        },
        disable: props.disabled,
        scrollable: true,
      });

      const translatedOsType = i18n.t(`devicesSettings.devicePostureTab.osTypes.${osType}`);
      dialogsStore.openDialog({
        ...config,
        header: {
          title: i18n.t(`modals.${action}.title`, { osType: translatedOsType }),
        },
      });
    };

    const addPolicyActions = [
      {
        action: DevicePostureAction.ADD,
        osType: DevicePostureOsType.WINDOWS,
        title: i18n.t(`devicesSettings.devicePostureTab.actions.${DevicePostureAction.ADD}`, {
          service: i18n.t(
            `devicesSettings.devicePostureTab.osTypes.${DevicePostureOsType.WINDOWS}`
          ),
        }),
      },
      {
        action: DevicePostureAction.ADD,
        osType: DevicePostureOsType.MAC_OS,
        title: i18n.t(`devicesSettings.devicePostureTab.actions.${DevicePostureAction.ADD}`, {
          service: i18n.t(`devicesSettings.devicePostureTab.osTypes.${DevicePostureOsType.MAC_OS}`),
        }),
      },
    ];

    // Removes the deleted scan schedule entry from the local list by matching id
    const onMalwareScanDelete = (payload: DialogDataConfig<MalwareScanItem>) => {
      localValue.value.devicePosture.malwareScansScheduleSettings =
        localValue.value.devicePosture.malwareScansScheduleSettings.filter(
          (s) => s.id !== payload.item.id
        );
    };

    // Replaces the edited scan schedule entry in place by matching id
    const onMalwareScanEdit = (payload: DialogDataConfig<MalwareScanItem>) => {
      const index = localValue.value.devicePosture.malwareScansScheduleSettings.findIndex(
        (s) => s.id === payload.item.id
      );
      if (index !== -1) {
        localValue.value.devicePosture.malwareScansScheduleSettings[index] = payload.item;
      }
    };

    // Appends a newly created scan schedule entry with a temporary client-side id
    const onMalwareScanCreate = (payload: DialogDataConfig<MalwareScanItem>) => {
      localValue.value.devicePosture.malwareScansScheduleSettings.push({
        ...payload.item,
        // temporary adding fake id to be able to edit it if needed
        id: new Date().getTime().toString(),
      });
    };

    const onMalwareScanAction = ({ item, action }: DialogDataConfig<MalwareScanItem>) => {
      let dialogConfig: DialogConfig;
      switch (action) {
        case MalwareScanActions.DELETE:
          dialogConfig = confirmationDialogsConfigConstructor({
            item,
            action,
            callback: onMalwareScanDelete,
            disable: props.disabled,
          });
          break;
        case MalwareScanActions.EDIT:
          dialogConfig = componentDialogsConfigConstructor({
            item,
            action,
            component: MalwareScanModal,
            width: ModalWidth.LARGE,
            callback: onMalwareScanEdit,
            disable: props.disabled,
          });
          break;
        case MalwareScanActions.CREATE:
          dialogConfig = componentDialogsConfigConstructor({
            item,
            action,
            component: MalwareScanModal,
            width: ModalWidth.LARGE,
            callback: onMalwareScanCreate,
            disable: props.disabled,
          });
          break;
      }
      dialogsStore.openDialog(dialogConfig!);
    };

    const addMalwareScanSchedule = () => {
      onMalwareScanAction({
        item: {
          // temporary adding fake id to be able to edit it if needed
          id: new Date().getTime().toString(),
          ...cloneDeep(defaultMalwareScanItem),
        },
        action: MalwareScanActions.CREATE,
      });
    };

    return {
      localValue,
      selectedTab,
      tabs,
      availableLabels,
      onChangeNgavItem,
      handleActionClick,
      handleAddPolicy,
      addPolicyActions,
      showAddMalwareScanBtn,
      mappedPostureData,
      onMalwareScanAction,
      addMalwareScanSchedule,
    };
  },
});
</script>

<style scoped lang="scss"></style>
